import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { IChampPersoViewModel } from 'src/app/pages/champs-perso/models/champ-perso-models';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { FileDownloaderService } from '../../common/file-downloader.service';
import { UploadUtilService } from '../../common/photo-uploader/upload-util.service';
import { DocumentReadModel } from '../../models/documents-service/document-read-model';
import { ConventionModel } from '../../models/entreprise/convention.model';
import { EntrepriseModel } from '../../models/entreprise/entreprise.model';
import { ModeReceptionFactureElectroniqueModel } from '../../models/entreprise/parametres-facturation-electronique.model';
import { TypeModel } from '../../models/entreprise/types.model';
import { ValeurListeFixeModel } from '../../models/listes-fixes/valeur-liste-fixe.model';
import { SessionColonneChampAdditionnel } from '../../models/sessions/session-colonne-champ-additionnel.model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { FileConversionsUtil, base64string } from '../../utils/file-conversions-util';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class EntreprisesService {
  private readonly baseUrlApi = this.configService.config.urlApi;;

  constructor(
    private oDataAdaptor: ODataAdaptor,
    private http: HttpClient,
    private notificationToastService: NotificationToastService,
    private fileDownloaderService: FileDownloaderService,
    private uploadUtilService: UploadUtilService,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  buildDataManagerGetEntreprises(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/entreprisesView`,
      adaptor: this.oDataAdaptor,
    });
  }

  addEntreprise(entreprise: EntrepriseModel): Observable<number> {
    return this.http
      .put<number>(`${this.baseUrlApi}/api/crm/entreprises/addEntreprise`, entreprise)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel("L'entreprise a bien été créée."))));
  }

  updateEntreprise(entreprise: EntrepriseModel): Observable<any> {
    return this.http
      .post(`${this.baseUrlApi}/api/crm/entreprises/updateEntreprise`, entreprise)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel("L'entreprise a bien été modifiée."))));
  }

  getEntrepriseDetaillee(number: number, code?: number): Observable<EntrepriseModel> {
    if(code){
      return this.http.get<EntrepriseModel>(`${this.baseUrlApi}/api/crm/entreprises/getEntreprise/${number}?gestionDroits=${code}`);
    }else{
      return this.http.get<EntrepriseModel>(`${this.baseUrlApi}/api/crm/entreprises/getEntreprise/${number}`);
    }
  }

  chargerTypeSociete(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/ListeFixeTypesSociete/getValeurs`);
  }

  chargerTypeEmployeur(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/ListeFixeTypesEmployeur/getValeurs`);
  }

  chargerTypeEmployeurSpecifique(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/ListeFixeTypesEmployeurSpecifique/getValeurs`);
  }

  chargerEtatsEntreprise(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/ListeFixeEtatsEntreprise/getValeurs`);
  }

  chargerCategoriesEntreprise(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/entreprises/GetCategories`);
  }

  chargerConventionsCollectives(): Observable<ConventionModel[]> {
    return this.http.get<ConventionModel[]>(`${this.baseUrlApi}/api/crm/entreprises/getConventions`);
  }

  chargerTypesConvention(): Observable<TypeModel[]>  {
    return this.http.get<TypeModel[]>(`${this.baseUrlApi}/api/crm/entreprises/GetTypesConvention`);
  }

  chargerModesReceptionFactureElectronique(): Observable<ModeReceptionFactureElectroniqueModel[]> {
    return this.http.get<ModeReceptionFactureElectroniqueModel[]>(
      `${this.baseUrlApi}/api/crm/entreprises/GetModesReceptionFactureElectronique`,
    );
  }

  activerDesactiverEntreprise(entreprise: EntrepriseModel, activer: boolean) {
    const statusText = activer === true ? 'activée' : 'désactivée';
    return this.http
      .post(`${this.baseUrlApi}/api/crm/entreprises/updateentrepriseactive`, {
        id: entreprise.id,
        actif: activer,
      })
      .pipe(
        tap(() => (entreprise.actif = activer)),
        tap(() => this.notificationToastService.push(new ToastSuccesModel(`L'entreprise a été ${statusText}`))),
      );
  }

  chargerLogo(idEntreprise: number): Observable<base64string> {
    return this.fileDownloaderService
      .fetchFile(`${this.baseUrlApi}/api/crm/Entreprises/GetLogo/${idEntreprise}`, null)
      .pipe(
        switchMap((blob: Blob) => {
          if (blob.size == 0) {
            return of(null);
          } else {
            return FileConversionsUtil.imageBlobToBase64(blob);
          }
        }),
      );
  }

  televerserLogo(photo: File, idEntreprise: number) {
    return this.uploadUtilService
      .uploadFile(`${this.baseUrlApi}/api/crm/Entreprises/DefinirLogo`, {File: photo, idEntreprise})
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Fichier téléversé avec succès`))));
  }

  supprimerLogo(idEntreprise: number): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrlApi}/api/crm/Entreprises/DeleteLogo/${idEntreprise}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Logo supprimé avec succès`))));
  }

  buildDataManagerGetDocumentsEntreprise(idEntreprise) {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/entreprises/GetDocuments/${idEntreprise}`,
      adaptor: this.oDataAdaptor,
    });
  }

  buildDataManagerGetEmailsEntreprise(idEntreprise) {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/entreprises/GetEmails/${idEntreprise}`,
      adaptor: this.oDataAdaptor,
    });
  }

  getDocumentDownloadUrl(id: number): string {
    return `${this.baseUrlApi}/api/crm/entreprises/GetDocument/${id}`;
  }

  supprimerDocument(idDocument: string):Observable<any> {
    return this.http.delete(`${this.baseUrlApi}/api/crm/entreprises/DeleteDocument/${idDocument}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Document supprimé avec succès`))));
  }

  televerserDocument(document: File, idEntreprise: number, updateFunction: () => any = null): Observable<DocumentReadModel> {
    return this.uploadUtilService
      .uploadFile(`${this.baseUrlApi}/api/crm/entreprises/AddDocument`, {File: document, idEntreprise}, updateFunction)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Fichier téléversé avec succès`))));
  }

  public getStates(): Observable<ValeurListeFixeModel[]>{
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/ListeFixeEtatsEntreprise/getValeurs`);
  }

  public getCompanyTypeForParam(code: number): Observable<ValeurListeFixeModel[]>{
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/crm/ListeFixeTypesSociete/getValeursByCodeOperation/${code}`);
  }

  public getExportCompletUrl(){
    return `${this.baseUrlApi}/api/crm/entreprises/ExporterExcel`
  }

  public getChampsAdditionnels(): Observable<IChampPersoViewModel[]>{
    return this.http.get<IChampPersoViewModel[]>(`${this.baseUrlApi}/api/crm/ChampsPersoEntreprise/GetChamps`)
  }

  public getColonnesChampsAdditionnelsEntreprise(): Observable<SessionColonneChampAdditionnel[]> {
    return this.http.get<Record<string, unknown>[]>(
      `${this.baseUrlApi}/api/crm/Entreprises/GetColonnesChampsAdditionnelsEntreprise`,
    ).pipe(
      map(colonnes => (colonnes ?? []).map(raw => ({
        guid: String(raw['guid'] ?? raw['Guid'] ?? ''),
        nom: String(raw['nom'] ?? raw['Nom'] ?? ''),
        type: Number(raw['type'] ?? raw['Type'] ?? 0),
        values: (raw['values'] ?? raw['Values'] ?? null) as string[] | null,
        cle: (raw['cle'] ?? raw['Cle'] ?? null) as string | null,
      }))),
    );
  }
}
